<template>
    <transition name="fade">
        <div class="spotify-popup modal show p-2">
            <div class="modal-backdrop p-2" @click="closeByBackdrop"></div>
            <div class="modal-content p-4 relative" :style="[list.length ? { height: '100% !important' } : {}]">
                <InnerLoader v-if="isLoading" />

                <div class="modal-header">
                    <div class="flex justify-between items-center mb-5">
                        <h5 class="modal-title font-size-16 md:font-size-20 my-0 shadow-text-none">
                            {{ $t('question_song_modal_header') }}
                        </h5>
                        <div class="text-right">
                            <i
                                v-if="showCloseButton"
                                @click="closeModal"
                                class="fas fa-times text-xl shadow-text-none cursor-pointer"
                            ></i>
                        </div>
                    </div>

                    <form @submit="onSubmitSearchTrackHandler" class="md:flex form">
                        <input
                            v-model="search.keyword"
                            :class="{ 'auth-input flex-1 md:mr-2 mb-2 input': true }"
                            :placeholder="$t('question_song_modal_placeholder_search')"
                            type="text"
                        />
                        <button
                            type="submit"
                            class="btn btn-primary w-100 w-md-auto font-size-16 font-opensans mb-2 system-button-color system-button-text-color"
                        >
                            {{ $t('question_song_modal_button_search') }}
                        </button>
                    </form>
                </div>

                <div class="modal-body w-full overflow-y-auto">
                    <div v-if="list.length > 0">
                        <div :key="index" v-for="(item, index) in list" :id="`track-item-${index}`">
                            <div class="flex md:items-center music-list justify-between track py-2">
                                <div class="flex music-block">
                                    <spotify-player
                                        :track-image="item.album.images[0].url"
                                        :track-id="item.id"
                                        :track-url="item.preview_url"
                                        :track-playing="trackPlayingId"
                                        @playing="onSpotifyPlaying"
                                        :audio="audio"
                                    />
                                    <div class="flex-1 overflow-hidden shadow-text-none">
                                        <h5 class="font-size-16 mt-0 mb-1 text-ellipsis">{{ item.name }}</h5>
                                        <p class="mb-0 font-size-14 text-ellipsis">
                                            {{ $t('question_artists') }} : {{ item.artists[0].name }}
                                        </p>
                                        <p class="mb-0 font-size-14 text-ellipsis">
                                            {{ $t('question_albums') }} : {{ item.album.name }}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    class="btn select-btn font-size-16 font-opensans system-button-color system-button-text-color"
                                    title="Add song to question"
                                    @click="onSelectTrack(item)"
                                >
                                    {{ $t('question_song_modal_button_select') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center font-size-14 md:font-size-16 py-12 px-4 bg-grey-light rounded">
                        {{ empty }}
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import InnerLoader from '@/components/InnerLoader'
import axios from 'axios'
import appConfig from '@/app.config.js'
import SpotifyPlayer from '@/components/SpotifyPlayer'
import gsap from 'gsap'
import popup from '@/lib/popup'
import Selection from '@/components/v2/Selection'

export default {
    name: 'spotify-select-popup',

    components: {
        InnerLoader,
        SpotifyPlayer,
        Selection
    },

    props: {
        showCloseButton: {
            type: Boolean,
            default: true
        },

        backdropAnimate: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            search: {
                keyword: '',
                type: 'all'
            },
            token: null,
            list: [],
            empty: this.$t('question_song_modal_list'),

            trackPlayingId: null,
            audio: document.createElement('AUDIO'),
            isLoading: false
        }
    },

    methods: {
        closeByBackdrop(e) {
            if (!this.backdropAnimate) return

            let duration = 0.2

            if (!e.target.classList.contains('modal-backdrop')) {
                return
            }

            // this.$emit('close')
            gsap.to('.modal-content', { scale: 1.05, transformOrigin: 'top center', duration: duration })
            gsap.to('.modal-content', { scale: 1, duration: duration, delay: duration })
        },

        closeModal() {
            this.$emit('close')
            this.audio.pause()
        },

        onSubmitSearchTrackHandler(e) {
            const options = {
                params: {
                    keyword: this.search.keyword
                }
            }

            this.trackPlayingId = null
            this.isLoading = true

            axios
                .get(appConfig.api.searchMusic, options)
                .then(res => {
                    const tracks = res.data.tracks
                    this.list = tracks.items

                    if (this.list.length === 0) {
                        this.empty = 'Not found.'
                    }

                    this.isLoading = false
                })
                .catch(err => {
                    this.isLoading = false
                    alert(err.response.data.error.message)
                })

            e.preventDefault()
        },

        onSelectTrack(track) {
            let transformData = {
                id: track.id,
                name: track.name,
                artists_name: track.artists[0].name,
                album_name: track.album.name,
                preview_url: track.preview_url,
                album_image: track.album.images[0].url
            }

            this.isLoading = true
            this.$store
                .dispatch('cacheSpotifySong', transformData)
                .then(res => {
                    this.isLoading = false
                    transformData.cache_preview_url = res.data.data.path
                    this.audio.pause()
                    this.$emit('selected', transformData)
                })
                .catch(() => {
                    this.isLoading = false
                    popup.alert('Cannot load spotify data')
                })
        },

        onSpotifyPlaying(id) {
            this.trackPlayingId = id
        }
    }
}
</script>

<style lang="scss">
$input-border-color: #cbcbcb;
$input-border-darker-color: #b6b6b6;

.spotify-popup {
    background-color: rgba(0, 0, 0, 0.2);

    .modal-backdrop {
        width: 100%;
        height: 100%;
        position: fixed;
        top: 0;
        left: 0;
    }

    .modal-content {
        background-color: #fff;
        max-width: 720px;
        max-height: 720px;
        width: 100%;
        height: auto !important;
        overflow: hidden;
        border-radius: 3px;
        box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);

        @media screen and (max-width: 767px) {
            width: 95%;
            height: 95%;
            margin: 0 !important;
        }

        .modal-body {
            // margin: 0 0 2rem 0;
            height: calc(100% - 95px);

            @media screen and (max-width: 719px) {
                margin: 0 0 1rem 0;
            }

            .music-list {
                column-gap: 16px;

                .music-block {
                    column-gap: 16px;
                    width: calc(100% - 156px);

                    .text-ellipsis {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }

                .select-btn {
                    width: 140px;
                    height: 38px;
                    background-color: var(--button-background-color);
                }

                @media screen and (max-width: 767px) {
                    flex-direction: column;
                    row-gap: 16px;

                    .music-block {
                        width: 100%;
                    }

                    .select-btn {
                        width: 100%;
                    }
                }
            }
        }
    }

    .auth-input {
        display: block;
        width: auto;
        height: 40px;
        border: 1px solid $input-border-color;
        font-size: 1rem;
        border-radius: 3px;
        text-align: center;

        &::placeholder {
            font-weight: bold;
            text-align: center;
            color: $input-border-color;
            text-transform: none;
        }

        &.is-invalid {
            border-color: #ef0000 !important;
        }
    }
}

.form {
    @media (max-width: 767px) {
        & > * {
            width: 100% !important;
        }
    }
}

.track {
    border-bottom: 1px solid #eaeaea;
}
</style>
